import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import toast from "react-hot-toast";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import VideoCheckbox from "./VideoCheckbox";
import { useCreateVideoEntryMutation } from "../../../features/api/videoApiSlice";
import LoadingSpinner from "../../loader/Loader";
import { useGetCategoriesQuery } from "../../../features/api/videoCategoriesApiSlice";
import { useGetWooProductsQuery } from "../../../features/api/wooApiSlice";
import { FileUploader } from "../../uploads/FileUploader";
import { customSelectStyles } from "../../../util/customSelectStyles";

const AddVideo = () => {
  const navigate = useNavigate();
  const { data: categories, isLoading: categoriesLoading } =
    useGetCategoriesQuery();
  const [uploadedVideoUrls, setUploadedVideoUrls] = useState(null);
  const [videoUploadState, setVideoUploadState] = useState({});
  const [createVideoEntry, { isLoading: isCreatingVideoEntry }] =
    useCreateVideoEntryMutation();

  const [filters, setFilters] = useState({
    search: "",
  });

  const {
    data: products = [],
    isLoading: productsLoading,
    isError,
    isFetching,
    refetch,
  } = useGetWooProductsQuery(filters);

  // Handle filter changes and trigger a refetch
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  useEffect(() => {
    // Only refetch if filters have changed
    refetch();
  }, [filters, refetch]);

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    if (!uploadedVideoUrls) {
      console.log("uploadedVideoUrls11", uploadedVideoUrls);
      toast.error("Please upload a video first.");
      setSubmitting(false);
      return;
    }
    console.log("uploadedVideoUrls22", { ...uploadedVideoUrls });
    console.log("uploadedVideoUrls33", uploadedVideoUrls);
    try {
      const response = await createVideoEntry({
        ...values,
        ...uploadedVideoUrls,
        products: values.products, // Pass the selected product IDs
      }).unwrap();

      toast.success(response?.message || "Video entry created successfully!");
      resetForm();
      setUploadedVideoUrls(null);
      setVideoUploadState({});
      navigate("/admin/videos");
    } catch (error) {
      console.error("Failed to create video entry:", error);
      toast.error(error?.data?.message || "Failed to create video entry.");
    } finally {
      setSubmitting(false);
    }
  };

  const categoryOptions = categories?.map((category) => ({
    value: category._id,
    label: category.name,
  }));

  if (isCreatingVideoEntry || categoriesLoading) return <LoadingSpinner />;

  const validateData = Yup.object().shape({
    videoTitle: Yup.string().required("Video title is required"),
    videoDescription: Yup.string().required("Video description is required"),
    categories: Yup.array()
      .min(1, "At least one category is required")
      .required("Video category is required"),
    videoType: Yup.string().required("Video type is required"),
  });

  return (
    <div className="dashboard-wrapper">
      <div className="dashboard-top-section">
        <div className="dash-top-flex">
          <h1 className="dashboard-title">Add Video</h1>
          <button onClick={() => navigate("/admin/videos/add-video")}>
            Add Video
          </button>
        </div>
      </div>
      <Formik
        initialValues={{
          featured: false,
          published: false,
          videoTitle: "",
          videoDescription: "",
          categories: [],
          videoType: "",
          products: [],
        }}
        validationSchema={validateData}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <Form className="dashboard-form">
            <div>
              <label htmlFor="videoTitle">
                Title<span className="required-field">*</span>
              </label>
              <Field type="text" name="videoTitle" />
              <ErrorMessage
                name="videoTitle"
                component="div"
                className="error"
              />
            </div>

            <div className="form-controls">
              <VideoCheckbox
                id="featured"
                label="Featured Video"
                checked={values.featured}
                onChange={(e) => setFieldValue("featured", e.target.checked)}
              />
            </div>

            <div className="form-controls">
              <VideoCheckbox
                id="published"
                label="Publish Video"
                checked={values.published}
                onChange={(e) => setFieldValue("published", e.target.checked)}
              />
            </div>

            <div className="form-controls">
              <label htmlFor="videoType">
                Public or Private<span className="required-field">*</span>
              </label>
              <Field as="select" name="videoType">
                <option value="">Select video type</option>
                <option value="paid">Paid</option>
                <option value="free">Free</option>
              </Field>
              <ErrorMessage
                name="videoType"
                component="div"
                className="error"
              />
            </div>

            {/* Multi-category support */}
            <div className="form-group">
              <label>Video Categories</label>
              <Select
                isMulti
                options={categoryOptions}
                value={categoryOptions?.filter((opt) =>
                  values.categories.includes(opt.value)
                )}
                onChange={(options) =>
                  setFieldValue(
                    "categories",
                    options ? options.map((o) => o.value) : []
                  )
                }
                styles={customSelectStyles}
              />
            </div>

            <div>
              <label>Upload Video File</label>
              <FileUploader
                identifier="video"
                initialUrls={{
                  fullUrl: values?.fullUrl,
                  posterUrl: values?.posterUrl,
                  pdfUrls: values?.pdfUrls || [],
                }}
                onUpload={(urls) => {
                  // ✅ Update Formik (for editing / UI consistency)
                  if (urls.videoUrl) {
                    setFieldValue("fullUrl", urls.videoUrl);
                  }
                  if (urls.posterUrl) {
                    setFieldValue("posterUrl", urls.posterUrl);
                  }
                  if (urls.resources) {
                    setFieldValue("pdfUrls", urls.resources);
                  }

                  // ✅ THIS IS THE MISSING PIECE
                  setUploadedVideoUrls({
                    fullUrl: urls.videoUrl,
                    previewUrl: urls.previewUrl,
                    thumbUrl: urls.thumbUrl,
                    posterUrl: urls.posterUrl,
                    resources: urls.resources || [],
                  });
                }}
              />
            </div>

            <div>
              <label htmlFor="videoDescription">
                Description<span className="required-field">*</span>
              </label>
              <ReactQuill
                className="custom-quill-editor"
                value={values.videoDescription || ""}
                onChange={(value) => setFieldValue("videoDescription", value)}
                name="videoDescription"
              />
              <ErrorMessage
                name="videoDescription"
                component="div"
                className="error"
              />
            </div>
            {/* --- Product Selector Section --- */}
            <div className="product-selection-section">
              <label>Linked Products</label>
              <div className="product-filter-bar">
                <input
                  type="text"
                  name="search"
                  placeholder="Search products by name..."
                  onChange={handleFilterChange}
                  value={filters.search}
                  className="product-search-input"
                />
              </div>
            </div>
            {/* Product Selector Section */}
            <div className="products-linked-area">
              <label>Linked Products</label>
              {(productsLoading || isFetching) && <LoadingSpinner />}
              {isError && (
                <p className="error-message">
                  Error fetching products. Please try again.
                </p>
              )}
              {products.length > 0 && (
                <div className="product-selector-grid">
                  {products.map((product) => {
                    const isSelected = values.products.includes(
                      product.id.toString()
                    );
                    return (
                      <div
                        key={product.id}
                        className={`product-card ${
                          isSelected ? "selected" : ""
                        }`}
                        onClick={() => {
                          if (isSelected) {
                            setFieldValue(
                              "products",
                              values.products.filter(
                                (id) => id !== product.id.toString()
                              )
                            );
                          } else {
                            setFieldValue("products", [
                              ...values.products,
                              product.id.toString(),
                            ]);
                          }
                        }}
                      >
                        {product.images && product.images.length > 0 && (
                          <img src={product.images[0].src} alt={product.name} />
                        )}
                        <div className="product-card-info">
                          <h4>{product.name}</h4>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="form-button"
            >
              Add Video
            </button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AddVideo;
